// src/components/order/ShippingStep.jsx
import React, { useState, useEffect } from 'react';

const InputField = ({ name, label, value, onChange, required = true, error = "" }) => (
  <div>
    <label htmlFor={name} className="block text-sm font-medium text-gray-700">{label}</label>
    <input
      type="text"
      id={name}
      name={name}
      value={value}
      onChange={onChange}
      required={required}
      className={`mt-1 block w-full px-3 py-2 bg-white border ${error ? 'border-red-500' : 'border-gray-300'} rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm`}
    />
    {error && <p className="text-xs text-red-600 mt-1">{error}</p>}
  </div>
);

const ShippingStep = ({ formData, setFormData }) => {
  const [phoneError, setPhoneError] = useState("");
  const [phoneWarning, setPhoneWarning] = useState("");
  const [pincodeError, setPincodeError] = useState("");
  const [cityLoading, setCityLoading] = useState(false);

  // Handle input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => {
      const updated = { ...prev, [name]: value };
      return updated;
    });
  };

  // Phone number validation
  const handlePhoneChange = (e) => {
    let value = e.target.value.replace(/\D/g, "");
    if (value.length > 10) {
      value = value.slice(0, 10);
      setPhoneWarning("Phone number cannot exceed 10 digits.");
    } else {
      setPhoneWarning("");
    }
    setFormData(prev => ({ ...prev, phone: value }));
    if (value.length < 10) {
      setPhoneError("Phone number must be exactly 10 digits.");
    } else {
      setPhoneError("");
    }
  };

  // Pincode auto city fetch
  useEffect(() => {
    const fetchCity = async () => {
      const pincode = formData.pincode;
      if (/^\d{6}$/.test(pincode)) {
        setCityLoading(true);
        setPincodeError("");
        try {
          const res = await fetch(`https://api.postalpincode.in/pincode/${pincode}`);
          const data = await res.json();
          if (data[0].Status === "Success" && data[0].PostOffice && data[0].PostOffice.length > 0) {
            const city = data[0].PostOffice[0].District;
            setFormData(prev => ({ ...prev, city }));
            setPincodeError("");
          } else {
            setPincodeError("Invalid PIN code or city not found.");
            setFormData(prev => ({ ...prev, city: "" }));
          }
        } catch (err) {
          setPincodeError("Failed to fetch city for this PIN code.");
          setFormData(prev => ({ ...prev, city: "" }));
        } finally {
          setCityLoading(false);
        }
      } else if (pincode && pincode.length > 0 && pincode.length < 6) {
        setPincodeError("PIN code must be 6 digits.");
        setFormData(prev => ({ ...prev, city: "" }));
      } else {
        setPincodeError("");
      }
    };
    fetchCity();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.pincode]);

  // States/UTs
  const indianStatesAndUTs = [
    "Andhra Pradesh", "Arunachal Pradesh", "Assam", "Bihar", "Chhattisgarh", "Goa",
    "Gujarat", "Haryana", "Himachal Pradesh", "Jharkhand", "Karnataka", "Kerala",
    "Madhya Pradesh", "Maharashtra", "Manipur", "Meghalaya", "Mizoram", "Nagaland",
    "Odisha", "Punjab", "Rajasthan", "Sikkim", "Tamil Nadu", "Telangana", "Tripura",
    "Uttar Pradesh", "Uttarakhand", "West Bengal",
    "Andaman and Nicobar Islands", "Chandigarh", "Dadra and Nagar Haveli and Daman and Diu",
    "Delhi", "Jammu and Kashmir", "Ladakh", "Lakshadweep", "Puducherry"
  ];

  return (
    <div>
      <h2 className="text-xl font-semibold text-gray-800 mb-6">Shipping Details</h2>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <InputField
          name="ReferenceID"
          label="Reference ID (Optional)"
          value={formData.ReferenceID}
          onChange={handleInputChange}
          required={false}
        />
        <InputField name="name" label="Full Name" value={formData.name} onChange={handleInputChange} />
        <InputField name="email" label="Email Address" value={formData.email} onChange={handleInputChange} />
        {/* Phone number field with +91 prefix and 10-digit limit */}
        <div>
          <label htmlFor="phone" className="block text-sm font-medium text-gray-700">Phone Number</label>
          <div className="mt-1 flex rounded-md shadow-sm">
            <span className="inline-flex items-center px-3 rounded-l-md border border-r-0 border-gray-300 bg-gray-50 text-gray-500 text-sm">
              +91
            </span>
            <input
              type="text"
              id="phone"
              name="phone"
              value={formData.phone}
              onChange={handlePhoneChange}
              maxLength={10}
              pattern="[0-9]{10}"
              className={`flex-1 min-w-0 block w-full px-3 py-2 rounded-r-md border ${phoneError || phoneWarning ? 'border-red-500' : 'border-gray-300'} bg-white placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm`}
            />
          </div>
          {phoneError && <p className="text-xs text-red-600 mt-1">{phoneError}</p>}
          {phoneWarning && <p className="text-xs text-red-600 mt-1">{phoneWarning}</p>}
        </div>
        <div className="md:col-span-2">
          <InputField name="address" label="Address" value={formData.address} onChange={handleInputChange} />
        </div>
          <InputField name="pincode" label="Pincode" value={formData.pincode} onChange={handleInputChange} error={pincodeError} />
        <div>
          <label htmlFor="state" className="block text-sm font-medium text-gray-700">State / UT</label>
          <select
            id="state"
            name="state"
            value={formData.state}
            onChange={handleInputChange}
            required
            className="mt-1 block w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
          >
            <option value="">Select a State/UT</option>
            {indianStatesAndUTs.map((state) => (
              <option key={state} value={state}>
                {state}
              </option>
            ))}
          </select>
        </div>
            <InputField name="city" label="City" value={formData.city} onChange={handleInputChange} error={cityLoading ? 'Fetching city...' : ''} />
      </div>
    </div>
  );
};

export default ShippingStep;